import { useRef } from 'react';
import { RoomPlayer } from '@airhead-games/shared/types';

/**
 * Animation configuration for a single avatar
 */
export interface AvatarEntranceConfig {
  shouldAnimate: boolean; // Whether this avatar should animate on entrance
}

/**
 * Configuration options for entrance animations
 */
export interface EntranceAnimationOptions {
  enabled: boolean; // Whether entrance animations are enabled
  duration?: number; // Animation duration (default: 4000ms)
  /**
   * Upper bound for total entrance animation time including stagger.
   * `shouldAnimate` is reported `true` for this long after a player is first
   * seen so that mid-flight re-renders (e.g. unrelated room state updates)
   * do not flip the Framer transition prop and cut the entrance short.
   */
  entrancePersistMs?: number;
}

/**
 * Return type for useAvatarEntrance hook
 */
export interface UseAvatarEntranceReturn {
  getPlayerConfig: (playerId: string) => AvatarEntranceConfig; // Animation configuration for a player
  duration: number; // Animation duration to pass to Framer Motion
}

/**
 * Hook for managing avatar entrance animations.
 *
 * The hook detects the first time it sees a player ID and reports
 * `shouldAnimate: true` for `entrancePersistMs` afterwards. We deliberately
 * mutate a ref during render (idempotent: `Set.add` and `Map.set`), and avoid
 * a `useEffect` snapshot of player IDs so that new players are detected on
 * the very render they mount. Without this, an unrelated re-render that
 * arrives mid-animation would flip the transition prop from the long entry
 * transition to the short reposition transition, and Framer would interrupt
 * and finish the entrance almost instantly.
 *
 * @param players - Array of current room players (unused; kept for API
 *   compatibility — detection happens in `getPlayerConfig`).
 * @param options - Animation configuration options
 * @returns Animation configurations and utilities
 */
export function useAvatarEntrance(
  _players: RoomPlayer[],
  options: EntranceAnimationOptions
): UseAvatarEntranceReturn {
  const { enabled, duration = 4000, entrancePersistMs } = options;

  // Map of playerId → timestamp (ms) of first sighting.
  const firstSeenAtRef = useRef<Map<string, number>>(new Map());

  const persistMs =
    typeof entrancePersistMs === 'number' ? entrancePersistMs : duration + 5000;

  const getPlayerConfig = (playerId: string): AvatarEntranceConfig => {
    if (!enabled) {
      return { shouldAnimate: false };
    }

    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const seenAt = firstSeenAtRef.current.get(playerId);

    if (seenAt === undefined) {
      firstSeenAtRef.current.set(playerId, now);
      return { shouldAnimate: true };
    }

    return { shouldAnimate: now - seenAt < persistMs };
  };

  return {
    getPlayerConfig,
    duration,
  };
}
